import { useEffect, useState } from 'react'
import { ButtonCommon, IconWrap, InputCommon, PageHeader, RequireTag, SelectCommon, UploadImage } from '../../components'
import headerImg from '../../assets/images/profileHeader.png'
import { Col, Form, Row, Select } from 'antd'
import styles from './index.module.scss'
import clsx from 'clsx'
import { PasswordModal } from './changePasswordModal'
import { EditIcon } from '../../assets/icons'
import { useGetProfile, usePostProfile } from '../../hooks/query/user'
import { Loading } from '../../components/Loading'
import { UploadFile } from 'antd/es/upload'
import { envApiUrl } from '../../apis/axiosClient'
import { getLanguageOptions, LanguageEnum, regex } from '../../until'
import { openNotificationWithIcon } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../../contexts/langContext'

export const Profile = () => {
  const [form] = Form.useForm()
  const [avatar, setAvatar] = useState<UploadFile>()
  const [showModal, setShowModal] = useState(false)
  const [isLoadingImg, setIsLoadingImg] = useState(true)
  const { isLoading: dataLoading, data, error } = useGetProfile()
  const [imgAvatar, setImgAvatar] = useState(data?.url_avatar)
  const [clearImg, setClearImg] = useState(false)
  const { mutateAsync, isLoading } = usePostProfile()
  const [isView, setIsView] = useState(true)
  const { t, i18n } = useTranslation()
  const {setLang} = useLanguage()
  const currentLang = localStorage.getItem('lang') || LanguageEnum.JAPANESE 
  const languageOptions = getLanguageOptions(t)
  const onFinish = async (values: any) => {
    delete values.password
    const langValue = languageOptions.find((option) => option.value === values.lang)?.value || LanguageEnum.JAPANESE
    try {
      await mutateAsync({
        ...values,
        avatar: avatar || data?.avatar,
        use_two_step_authentication: values.use_two_step_authentication === 1,
        lang: langValue
      })
      setLang(values.lang)
      i18n.changeLanguage(values.lang)
      openNotificationWithIcon('success', '', t('profile.message.updateSuccess'))
      setIsView(true)
    } catch (error: any) {
      if (error.errors) {
        const entries = Object.entries(error.errors)
        const arrError = entries.map((item: any) => ({ name: item[0], errors: item[1] }))
        form.setFields(arrError)
      } else {
        openNotificationWithIcon('error', '', error?.message)
      }
    }
  }
  const onHideModal = () => {
    setShowModal(false)
  }

  const oncancel = () => {
    setIsView(true)
    setClearImg(true)
    form.setFieldsValue({
      postal_code: data?.postal_code || '',
      full_name: data?.full_name || '',
      full_name_katakana: data?.full_name_katakana || '',
      email: data?.email || '',
      number_phone: data?.number_phone || '',
      address_1: data?.address_1 || '',
      address_2: data?.address_2 || '',
      username: data?.username || '',
      use_two_step_authentication: data?.use_two_step_authentication ? 1 : 0,
      lang: data?.lang || LanguageEnum.JAPANESE
    })
  }


  useEffect(() => {
    if (data) {
      const currentLangKey = languageOptions.find(
        (option) => option.value === data.lang
      )
      setImgAvatar(data.url_avatar)
      form.setFieldsValue({
        postal_code: data.postal_code || '',
        full_name: data.full_name || '',
        full_name_katakana: data.full_name_katakana || '',
        email: data.email || '',
        number_phone: data.number_phone || '',
        address_1: data.address_1 || '',
        address_2: data.address_2 || '',
        username: data.username || '',
        use_two_step_authentication: data?.use_two_step_authentication ? 1 : 0,
        lang: currentLangKey?.value || LanguageEnum.JAPANESE
      })
      if (!data.avatar) {
        setIsLoadingImg(false)
      }
      return
    }
  }, [data])

  useEffect(() => {
    if (error) {
      setIsLoadingImg(false)
      openNotificationWithIcon('error', '', error?.message)
    }
  }, [error])

  return (
    <div>
      <Loading isLoading={dataLoading || isLoadingImg} />
      <PageHeader title={t('profile.profile')} backgroundImage={headerImg} height="108px"></PageHeader>
      <div className={styles.content}>
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" className={styles.form}>
          <div className={clsx(styles.form__avatar)}>
            <UploadImage
              useDefaultAvatar={true}
              url={imgAvatar ? `${envApiUrl}${imgAvatar}` : ''}
              onChangeAvatar={setAvatar}
              setLoadingImg={setIsLoadingImg}
              title=""
              hasBorder={false}
              className={clsx(styles.form__avatar__upload)}
              isAllow={isView}
              onClear={clearImg}
              setClear={() => setClearImg(false)}
            />
          </div>
          <div className={styles['form__control--container']}>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={
                    <>
                      {t('profile.name')}
                      {!isView ? <RequireTag /> : null}
                    </>
                  }
                  name="full_name"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      required: true,
                      message: t('profile.inputFullName'),
                    },
                  ]}
                  validateTrigger="onSubmit"
                >
                  <InputCommon
                    maxLength={50}
                    disabled={isView}
                    placeholder={isView ? '' : t('profile.placeholderFullName')}
                    onBlur={() => {
                      form.setFieldValue('full_name', form.getFieldValue('full_name')?.trim())
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={t('profile.nameKatakana')}
                  name="full_name_katakana"
                  className={styles['form__item--half']}
                >
                  <InputCommon
                    maxLength={50}
                    disabled={isView}
                    placeholder={isView ? '' : t('profile.placeholderKatakana')}
                    onBlur={() => {
                      form.setFieldValue('full_name_katakana', form.getFieldValue('full_name_katakana')?.trim())
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  validateTrigger="onSubmit"
                  label={
                    <>
                      {t('profile.username')}
                      {!isView ? <RequireTag /> : null}
                    </>
                  }
                  name="username"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      required: true,
                      message: t('profile.inputUserName'),
                    },
                    {
                      validator(_, value) {
                        if (value.indexOf(' ') > 0) {
                          return Promise.reject(`${t('staff.message.whiteSpace')}`)
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <InputCommon
                    maxLength={50}
                    disabled={isView}
                    placeholder={isView ? '' : t('profile.placeholderUsername')}
                    onBlur={() => {
                      form.setFieldValue('username', form.getFieldValue('username')?.trim())
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={t('profile.password')}
                  name="password"
                  className={clsx(styles.form__item, styles['form__item--half'])}
                >
                  <div>
                    <InputCommon type={'password'} placeholder="●●●●●●●●" disabled={true} />
                    <IconWrap
                      icon={<EditIcon />}
                      className={styles.form__item__edit}
                      onClick={() => {
                        setShowModal(true)
                      }}
                    />
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item label={t('profile.email')} name="email" className={styles['form__item--half']}>
                  <InputCommon
                    maxLength={150}
                    placeholder={isView ? '' : t('profile.placeholderEmail')}
                    disabled={true}
                    onBlur={() => {
                      form.setFieldValue('email', form.getFieldValue('email')?.trim())
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={t('profile.phoneNumber')}
                  name="number_phone"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      pattern: regex.validatePhone,
                      message: t('staff.message.invalidPhone'),
                    },
                  ]}
                  validateTrigger="onSubmit"
                >
                  <InputCommon
                    maxLength={11}
                    disabled={isView}
                    placeholder={isView ? '' : t('profile.placeholderPhoneNumber')}
                    onBlur={() => {
                      form.setFieldValue('number_phone', form.getFieldValue('number_phone')?.trim())
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={t('profile.address_1')}
              name="address_1"
              className={styles['form__item--full']}
              initialValue={''}
            >
              <InputCommon
                maxLength={50}
                disabled={isView}
                placeholder={isView ? '' : t('profile.placeholderAddress1')}
                onBlur={() => {
                  form.setFieldValue('address_1', form.getFieldValue('address_1')?.trim())
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('profile.address_2')}
              name="address_2"
              className={styles['form__item--full']}
              initialValue={''}
            >
              <InputCommon
                maxLength={50}
                disabled={isView}
                placeholder={isView ? '' : t('profile.placeholderAddress2')}
                onBlur={() => {
                  form.setFieldValue('address_2', form.getFieldValue('address_2')?.trim())
                }}
              />
            </Form.Item>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={t('profile.postal_code')}
                  name="postal_code"
                  className={styles['form__item--full']}
                  initialValue={''}
                  rules={[
                    {
                      pattern: regex.validatePostalCode,
                      message: t('profile.invalidPostalCode'),
                    },
                  ]}
                  validateTrigger="onSubmit"
                >
                  <InputCommon
                    maxLength={8}
                    disabled={isView}
                    placeholder={isView ? '' : t('profile.placeholderPostalCode')}
                    onBlur={() => {
                      form.setFieldValue('postal_code', form.getFieldValue('postal_code')?.trim())
                    }}
                  />
                </Form.Item></Col>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={
                    <>
                      {t('staff.addModal.twoFactorAuthentication')}
                    </>
                  }
                  rules={[
                    {
                      required: true,
                      message: t('staff.addModal.twoFactorAuthentication') + ' ' + t('manager.message.isRequired'),
                    },
                  ]}
                  name="use_two_step_authentication"
                  className={styles['form__item--half']}
                  validateTrigger="onSubmit"
                >
                  <SelectCommon 
                    disabled={isView}
                    autoFocus={false}
                    maxTagCount="responsive"
                    placeholder={t('staff.addModal.twoFactorAuthenticationPlaceholder')}
                  >
                    <Select.Option value={1}>{t('staff.addModal.active')}</Select.Option>
                    <Select.Option value={0}>{t('staff.addModal.inactive')}</Select.Option>
                  </SelectCommon>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={
                    <>
                      {t('profile.languageChange')}
                    </>
                  }
                  name="lang"
                  className={styles['form__item--half']}
                  validateTrigger="onSubmit"
                >
                  <SelectCommon
                    disabled={isView} 
                    autoFocus={false}
                    maxTagCount="responsive"
                    defaultValue={currentLang}
                  >
                    {languageOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </SelectCommon>
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className={styles.form__btn}>
            {isView ? (
              <ButtonCommon lang={i18n.language} type="ghost" onClick={() => setIsView(false)} >
                {t('profile.edit')}
              </ButtonCommon>
            ) : (
              <>
                <ButtonCommon type="ghost" onClick={oncancel}>
                  {t('profile.cancel')}
                </ButtonCommon>
                <ButtonCommon loading={isLoading} type="primary" htmlType="submit" lang={i18n.language}>
                  {t('profile.save')}
                </ButtonCommon>
              </>
            )}
          </div>
        </Form>
        <PasswordModal visible={showModal} onOk={onHideModal} onCancel={onHideModal} />
      </div>
    </div>
  )
}
